import { I18n } from "@/i18n/I18n";
import { Plus, Search } from "lucide-react";
import { useState } from "react";
import { Form, Modal, Spinner } from "react-bootstrap";
import { useRequirementsStore } from "@/frontend/store/RequirementsStore";
import { useProcedureListStore } from "@/frontend/store/ProcedureListStore";
import { GeneratedProcedure } from "../procedures/GeneratedProcedure";

interface AssignProcedureTemplateModalProps {
  assignedIds: string[];
  requirementId: string
}

export function AssignProcedureModal({ 
  assignedIds, 
  requirementId
}: AssignProcedureTemplateModalProps) {
  const createAssignment = useRequirementsStore(state => state.createAssignment);
  const procedures = useProcedureListStore(state => state.procedures);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProcedureIds, setSelectedProcedureIds] = useState<string[]>([]);
  const [isAssigning, setIsAssigning] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const availableProcedures = procedures.filter(
    procedure => !assignedIds.includes(procedure.id)
  );

  const filteredProcedures = availableProcedures.filter(procedure =>
    procedure.title?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const toggleProcedureSelection = (procedureId: string) => {
    setSelectedProcedureIds(prev => 
      prev.includes(procedureId) 
        ? prev.filter(id => id !== procedureId)
        : [...prev, procedureId]
    );
  };

  const handleAssign = async () => {
    if (selectedProcedureIds.length === 0) return;

    setIsAssigning(true);
    try {
      for (const procedureId of selectedProcedureIds) {
        await createAssignment(requirementId, procedureId);
      }
      setShowModal(false);
    } finally {
      setIsAssigning(false);
      setSelectedProcedureIds([]);
    }
  };

  const handleClose = () => {
    setShowModal(false);
    setSelectedProcedureIds([]);
    setSearchQuery('');
  };

  const allProceduresAssigned = availableProcedures.length === 0;
  const assignDisabled = selectedProcedureIds.length === 0 || isAssigning;

  return (
    <>
      <button className="fusa-btn-medium-with-icon btn btn-primary" onClick={() => setShowModal(true)}>
        <Plus size={16} />
        {I18n.get('assignProcedure')}
      </button>
      <Modal
        show={showModal}
        onHide={handleClose}
        centered
        size="lg"
        dialogClassName="assign-template-modal"
      >
        <Modal.Body style={{ padding: 31 }}>
          <h5 style={{ fontSize: 20, fontWeight: 500, margin: 0 }}>
            {I18n.get('assignProcedures')}
          </h5>
          <p style={{ fontSize: 14, color: 'var(--neutral-700)', margin: '4px 0 0' }}>
            {I18n.get('assignProceduresDescription')}
          </p>

          {allProceduresAssigned ? (
            <p className="text-center" style={{ color: 'var(--neutral-500)', padding: '40px 0' }}>
              {I18n.get('allProceduresAssigned')}
            </p>
          ) : (
            <>
              <div className="d-flex justify-content-between align-items-end" style={{ marginTop: 20, marginBottom: 8 }}>
                <span className="list-card-title">{I18n.get('availableProcedures')}</span>
                <div className="assign-template-search form-control-neutral">
                  <Search size={20} style={{ color: 'var(--neutral-500)', flexShrink: 0 }} />
                  <input
                    className="form-control-neutral"
                    type="text"
                    placeholder={I18n.get('searchProcedures')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>

              <div className="template-list-container">
                {filteredProcedures.length === 0 ? (
                  <p className="text-center" style={{ color: 'var(--neutral-500)', margin: 0 }}>
                    {I18n.get('noProceduresMatchSearch')}
                  </p>
                ) : (
                  filteredProcedures.map(procedure => (
                    <div
                      key={procedure.id}
                      className={`template-list-item ${selectedProcedureIds.includes(procedure.id) ? 'selected' : ''}`}
                      onClick={() => toggleProcedureSelection(procedure.id)}
                    >
                      <Form.Check
                        type="checkbox"
                        checked={selectedProcedureIds.includes(procedure.id)}
                        onChange={() => toggleProcedureSelection(procedure.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <GeneratedProcedure key={procedure.id} procedure={procedure} />
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="fusa-btn-medium btn-blue-light"
            onClick={handleClose}
          >
            {I18n.get('cancel')}
          </button>
          <button
            type="button"
            className="fusa-btn-medium fusa-btn-primary"
            onClick={() => void handleAssign()}
            disabled={assignDisabled}
          >
            {isAssigning ? (
              <>
                <Spinner size="sm" className="me-2" />
                {I18n.get('assigning')}
              </>
            ) : (
              I18n.get('assignProcedure')
            )}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
